import { environment } from "src/environments/environment";
import { AccountantService } from "./accountant.service";
import { QuickbooksIntegrationService } from "./../../quickbooks-integration.service";

import { HotToastService } from "@ngneat/hot-toast";

import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { TypeaheadMatch, TypeaheadModule } from "ngx-bootstrap/typeahead";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { DeleteConfirmationModalComponent } from "./modals/delete-confirmation-modal.component";
import { QboAccountsModalComponent } from "./qbo-accounts-modal/qbo-accounts-modal.component";
import { ActiveClientsOnlyPipe } from "../common/active-clients-only.pipe";
import { FormsModule } from "@angular/forms";
import { NgIf, NgFor } from "@angular/common";

const STATE_NO_INTEGRATED_WORKSPACES = 3;
const STATE_INTEGRATED_WORKSPACES = 4;

export interface QuickBooksData {
  uuid?: string;
  workspace_id: number;
  workspace_name: string;
  company_name?: string;
  account_name?: string;
  is_integrated: boolean;
  class_tracking: boolean;
  accounts?: any[];
}

/**
 *
 */
@Component({
    selector: "app-quickbooks-integration",
    templateUrl: "./quickbooks-integration.component.html",
    styleUrls: ["./quickbooks-integration.component.css"],
    imports: [
        NgIf,
        FormsModule,
        TypeaheadModule,
        NgFor,
        ActiveClientsOnlyPipe,
    ]
})
export class QuickBooksIntegrationComponent implements OnInit {
  quickBooksIntegrationStep = 3;
  loading = false;
  // workspaces: any[];
  quickbooksData: QuickBooksData[] = [];
  companiesAccounts: any = {};

  modalRef?: BsModalRef;

  @ViewChild("qboAccountsModal") private qboAccountsModal: TemplateRef<any>;
  @Input() workspaces: any[] = [];

  account: any = null;

  constructor(
    public toastr: HotToastService,
    private accountantService: AccountantService,
    private qbServices: QuickbooksIntegrationService,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.getConnectedWorkspaces();
  }

  selectAccount(linkUuid: string, event: TypeaheadMatch) {
    this.account = event.item;

    this.qbServices
      .updateConnectedWorkspace(
        linkUuid,
        event.item.id,
        event.item.name,
        event.item.path
      )
      .then(
        (res) => this.getConnectedWorkspaces(),
        (err) =>
          this.handleError(
            `Couldn't save the selected Account. Please try again later.`,
            err
          )
      );
  }

  toggleIntegrationStep(step: number) {
    this.quickBooksIntegrationStep = step;
  }

  selectWorkspaceForIntegration(event: TypeaheadMatch) {
    this.addWorkspaceForIntegration(event.item.id, event.item.name);
    this.toggleIntegrationStep(STATE_INTEGRATED_WORKSPACES);
  }

  private addWorkspaceForIntegration(
    workspace_id: number,
    workspace_name: string
  ) {
    const qbData = {
      workspace_id,
      workspace_name,
      class_tracking: false,
      is_integrated: false,
    };
    this.quickbooksData.push(qbData);
  }

  getConnectedWorkspaces() {
    this.loading = true;

    this.qbServices.getCompaniesAccounts().then((res) => {
      console.log("companiesAccounts", res);
      this.companiesAccounts = res;
    });

    this.qbServices.getConnectedWorkspaces().then(
      (res) => this.handleConnectedWorkspacesResponse(res),
      (err) => this.handleError(`Couldn't load connected workspaces.`, err)
    );
  }

  private handleConnectedWorkspacesResponse(res: QuickBooksData[]) {
    this.quickbooksData = [];
    if (res !== undefined && res.length > 0) {
      res.forEach((entry: QuickBooksData) => {
        if (entry.company_name) {
          this.quickbooksData.push({
            uuid: entry.uuid,
            workspace_id: entry.workspace_id,
            workspace_name: entry.workspace_name,
            company_name: entry.company_name,
            class_tracking: entry.class_tracking,
            is_integrated: true,
          });
        }
      });
      this.toggleIntegrationStep(STATE_INTEGRATED_WORKSPACES);
    } else {
      this.toggleIntegrationStep(STATE_NO_INTEGRATED_WORKSPACES);
    }
    this.loading = false;
  }

  launchAuthenticationForm(workspaceId: number) {
    this.loading = true;
    let isListenerExhausted = false;

    const path = `${environment.apiUrl}/integration/quickbooks/auth?integrate=${workspaceId}`;

    let parameters = "location=1,width=800,height=650";
    parameters +=
      ",left=" + (screen.width - 800) / 2 + ",top=" + (screen.height - 650) / 2;

    const win = window.open(path, "connectPopup", parameters);
    window.addEventListener(
      "message",
      (e: MessageEvent) => {
        try {
          const response = JSON.parse(e.data);
          if (response.type != "quickbooks" || isListenerExhausted) {
            console.log("not quickbooks response or listener is exhausted");
            return;
          }
          isListenerExhausted = true;
          if (response.status === "success") {
            this.getConnectedWorkspaces();
            this.toastr.success(`Authenticated in QuickBooks.`);
          } else {
            let errorMessage = response.message
              ? response.message
              : `Couldn't authenticate in QuickBooks.`;
            this.toastr.error(errorMessage);
            console.error(errorMessage);
          }
          win.close();
        } catch {
          return;
        }
      },
      false
    );
    const timer = setInterval(() => {
      if (win.closed) {
        this.loading = false;
        clearInterval(timer);
      }
    }, 1000);
  }

  disconnectWorkspaceConfirmationModal(linkUuid: string, companyName: string) {
    const initialState = {
      displayName: companyName,
    };

    const deleteModal = this.modalService.show(
      DeleteConfirmationModalComponent,
      { initialState }
    );
    deleteModal.content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.disconnectWorkspace(linkUuid);
      }
    });
  }

  disconnectWorkspace(linkUuid: string) {
    this.loading = true;
    this.qbServices.disconnectWorkspace(linkUuid).then(
      (res) => this.handleDisconnectResponse(res),
      (err) =>
        this.handleError(`Couldn't disconnect the selected workspace.`, err)
    );
  }

  refreshPreferences(workspaceUuid: string, idx: number) {
    this.loading = true;
    this.qbServices.refreshTrackingClassFlag(workspaceUuid).subscribe(
      (res: QuickBooksData) => {
        this.quickbooksData[idx].class_tracking = res.class_tracking;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.toastr.error(
          "Error refreshing your connection. Please remove this connection and try again."
        );
      }
    );
  }

  private handleDisconnectResponse(res: any) {
    if (res.status === "warning") {
      this.toastr.warning(res.message);
    } else {
      this.toastr.success(res.message);
    }
    this.loading = false;
    this.getConnectedWorkspaces();
  }

  private handleError(message: string, err: any) {
    this.loading = false;
    this.toastr.error(message);
    console.error(err);
  }

  loadAccounts(entry: QuickBooksData) {
    // this.loading = true;
    return this.qbServices
      .getAccountsByCompanyId(entry.workspace_id)
      .subscribe((res: any) => {
        return res;
      });
  }

  addAccountToCompany(workspaceId) {
    console.log(workspaceId, this.account);
    this.qbServices
      .addAccountToCompany(workspaceId, {
        accountId: this.account.id,
        accountName: this.account.name,
        accountPath: this.account.path,
      })
      .then((res) => {});
  }

  openQboAccountsModal(entry) {
    console.log(entry, this.companiesAccounts[entry.workspace_id]);
    this.modalService.show(QboAccountsModalComponent, {
      class: "modal-md modal-white-bg",
      initialState: {
        account: entry,
        companyAccounts:
          this.companiesAccounts[entry.workspace_id] != null
            ? this.companiesAccounts[entry.workspace_id]
            : [],
      },
    });
  }
}
